import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import DateFnsUtils from '@date-io/date-fns';
import { format, parseISO } from 'date-fns';
import {
  MuiPickersUtilsProvider,
  DatePicker,
} from '@material-ui/pickers';
import {
  Button,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  Icon,
  IconButton,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from '@material-ui/core';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import {
  orderStatus,
  nextechIdTypes,
  modmedIdTypes,
  statusMessages
} from 'helpers/orders';


const defaultCustomerGroups = [
  {
    id: 0,
    code: 'Not Logged In'
  }
];

const FilterDrawer = (
  {
    setOpenFilter,
    setFilterConditions,
    filterConditions,
    isNextechIntegrated,
    isModMedIntegrated,
    hiddenFilters = {}
  }: IFilterDrawerProps
) => {
  const [expandedDate, toggleExpandedDate] = useState(false);
  const [expandedName, toggleExpandedName] = useState(false);
  const [expandedEmail, toggleExpandedEmail] = useState(false);
  const [expandedStatus, toggleExpandedStatus] = useState(false);
  const [expandedCustomerGroup, toggleExpandedCustomerGroup] = useState(false);
  const [expandedSyncStatus, toggleExpandedSyncStatus] = useState(false);
  const [expandedModMedStatus, toggleExpandedModMedStatus] = useState(false);
  const [expandedRefundStatus, toggleExpandedRefundStatus] = useState(false);

  const hiddenFiltersKeys = Object.keys(hiddenFilters) || [];

  const [conditions, setConditions] = useState({
    created_from: {
      field: 'created_at',
      value: filterConditions.created_from?.value || '',
      conditionType: 'gt'
    },
    created_to: {
      field: 'created_at',
      value: filterConditions.created_to?.value || '',
      conditionType: 'lt'
    },
    customer_firstname: {
      field: 'customer_firstname',
      value: filterConditions.customer_firstname?.value || '',
      conditionType: 'like'
    },
    customer_lastname: {
      field: 'customer_lastname',
      value: filterConditions.customer_lastname?.value || '',
      conditionType: 'like'
    },
    customer_email: {
      field: 'customer_email',
      value: filterConditions.customer_email?.value || '',
      conditionType: 'like'
    },
    status: {
      field: 'status',
      value: filterConditions.status?.value || '',
      conditionType: 'eq'
    },
    customer_group_id: {
      field: 'customer_group_id',
      value: filterConditions.customer_group_id?.value || '',
      conditionType: 'in'
    },
    nextech: {
      field: 'nextech_id',
      value: filterConditions.nextech?.value || '',
      conditionType: filterConditions.nextech?.conditionType || 'eq'
    },
    modmed: {
      field: 'modmed_id',
      value: filterConditions.modmed?.value || '',
      conditionType: filterConditions.modmed?.conditionType || 'eq'
    },
    sync_error: {
      field: 'emr_sync_error',
      value: filterConditions.emr_sync_error?.value || '',
      conditionType: filterConditions.emr_sync_error?.conditionType || 'eq'
    },
    refund_status: {
      field: 'refund_status',
      value: filterConditions.refund_status?.value || '',
      conditionType: filterConditions.refund_status?.conditionType || 'eq'
    }
  });

  const {customerGroups } = useSelector(
    (state: {customers: ICustomersState}) => state.customers
  );

  const handleChangeFilter = (field, value, conditionType, name) => {
    setConditions((conditions) => (
      {
        ...conditions,
        [name]: {
          field,
          value,
          conditionType
        }
      }
    ));
  };

  const setConvertedDate = (value) => {
    const date = new Date(value);
    return date.toUTCString();
  };

  const handleClearDate = (event, name) => {
    event.preventDefault();
    event.stopPropagation();
    setConditions({
      ...conditions,
      [name]: {
        ...conditions['created_from'],
        value: ''
      }
    });
  };

  const handleApplyFilter = () => {
    setFilterConditions(conditions);
    setOpenFilter(false);
  };

  const handleChangeCustomerGroup = (checked, group) => {
    const groupIdsSelected = conditions.customer_group_id.value.length
      ? conditions.customer_group_id.value.split(',')
      : [];

    const value = checked
      ? [...groupIdsSelected, group.id].join(',')
      : groupIdsSelected.filter((id) => id != group.id).join(',');

    handleChangeFilter('customer_group_id', value, 'in', 'customer_group_id');
  };

  const isDatesError = conditions?.created_to.value !== '' && conditions?.created_to.value < conditions?.created_from?.value;

  return (
    <div className="px-4 pt-4 min-h-full-screen flex-column justify-between">
      <div>
        <div className="flex items-center justify-between mb-2">
          <Typography variant="h5" className="text-primary uppercase">Filter by:</Typography>
          <IconButton onClick={() => setOpenFilter(false)}>
            <Icon fontSize="small">close</Icon>
          </IconButton>
        </div>
        <div className="flex items-center flex-wrap mb-2">
          {Object.values(conditions).map((filterItem, index) => {
            if (!filterItem.value || filterItem.field === 'emr_sync_error' || hiddenFiltersKeys.includes(filterItem.field)) {
              return null;
            }

            let label = filterItem.value.replace(/%/g, '');
            let fieldName = filterItem.field;

            if (filterItem.field === 'customer_group_id') {
              const groupsIdsSelected = filterItem.value.length ? filterItem.value.split(',') : [];

              return (
                <>
                  {groupsIdsSelected.map((groupId) => {
                    const code = [...defaultCustomerGroups, ...customerGroups]
                      .find((group) => group.id == groupId)?.code;

                    if (!code) {
                      return null;
                    }

                    return (
                      <div key={`filter-group-${groupId}`} className="mr-2 mb-2">
                        <Chip
                          label={code}
                          onDelete={() => {
                            setConditions({
                              ...conditions,
                              customer_group_id: {
                                ...conditions.customer_group_id,
                                value: groupsIdsSelected.filter((id) => id != groupId).join(',')
                              }
                            }
                            );
                          }}
                        />
                      </div>
                    );
                  })
                  }
                </>
              );
            }

            if (filterItem.field === 'nextech_id') {
              fieldName = 'nextech';

              if (filterItem.value == nextechIdTypes.sync) {
                label = statusMessages.synced;
              }

              if (filterItem.value == nextechIdTypes.error) {
                label = statusMessages.error;
              }

              if (filterItem.value == nextechIdTypes.disabled) {
                label = statusMessages.disabled;
              }

              if (filterItem.value == nextechIdTypes.waitForSync) {
                label = statusMessages.pending;
              }
            }

            if (filterItem.field === 'modmed_id') {
              fieldName = 'modmed';

              if (filterItem.value == modmedIdTypes.sync) {
                label = statusMessages.synced;
              }

              if (filterItem.value == modmedIdTypes.error) {
                label = statusMessages.error;
              }

              if (filterItem.value == modmedIdTypes.disabled) {
                label = statusMessages.disabled;
              }

              if (filterItem.value == modmedIdTypes.waitForSync) {
                label = statusMessages.pending;
              }
            }

            if (filterItem.field === 'status') {
              label = orderStatus[filterItem.value.replace(/%/g, '')];
            }

            if (filterItem.conditionType === 'gt') {
              label = `From: ${format(parseISO(filterItem.value.replace(/%/g, '')), 'MM/dd/yyyy')}`;
              fieldName = 'created_from';
            }

            if (filterItem.conditionType === 'lt') {
              label = `To: ${format(parseISO(filterItem.value.replace(/%/g, '')), 'MM/dd/yyyy')}`;
              fieldName = 'created_to';
            }

            if (filterItem.field === 'refund_status') {
              const value = filterItem.value === 'no' ? 'not' : filterItem.value;
              label = `${value} refunded`;
            }

            return (
              <div key={`filter-${index}`} className="mr-2 mb-2 capitalize">
                <Chip
                  label={label}
                  onDelete={() => {
                    if (fieldName === 'modmed' || fieldName === 'nextech') {
                      setConditions({
                        ...conditions,
                        [fieldName]: {
                          ...conditions[fieldName],
                          value: ''
                        },
                        sync_error: {
                          ...conditions['sync_error'],
                          value: '',
                          conditionType: 'eq'
                        }
                      });
                    } else {
                      setConditions({
                        ...conditions,
                        [fieldName]: {
                          ...conditions[fieldName],
                          value: ''
                        }
                      });
                    }
                  }}
                />
              </div>
            );
          })}
        </div>
        <div
          className="flex items-center justify-between cursor-pointer"
          onClick={() => toggleExpandedDate((expandedDate) => !expandedDate)}
        >
          <Typography variant="h6">Purchase Date</Typography>
          {expandedDate ? <ExpandLess /> : <ExpandMore />}
        </div>
        <Collapse in={expandedDate} timeout="auto">
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <DatePicker
              disableFuture
              autoOk
              fullWidth
              variant="inline"
              format="MM/dd/yyyy"
              emptyLabel="mm/dd/yyyy"
              name="created_from"
              label="From"
              className="mt-5"
              value={
                conditions?.created_from?.value
                  ? setConvertedDate(conditions.created_from.value)
                  : null
              }
              onChange={(date) => handleChangeFilter(
                'created_at',
                `${format(date, 'yyyy-MM-dd 00:00:00')}`,
                'gt',
                'created_from'
              )
              }
              inputVariant="outlined"
              InputProps={{
                readOnly: true,
                startAdornment: (
                  <CalendarTodayIcon className="text-muted text-16" />
                ),
                endAdornment: (
                  <Icon
                    className="cursor-pointer"
                    onClick={(event) => handleClearDate(event, 'created_from')}
                  >
                    close
                  </Icon>
                )
              }}
              error={isDatesError}
              helperText={
                isDatesError
                && '"From" Date can not be later than "To" Date'
              }
            />
            <DatePicker
              disableFuture
              autoOk
              fullWidth
              variant="inline"
              format="MM/dd/yyyy"
              emptyLabel="mm/dd/yyyy"
              name="created_to"
              className="mt-5"
              label="To"
              value={
                conditions?.created_to?.value
                  ? setConvertedDate(conditions.created_to.value)
                  : null
              }
              onChange={(date) => handleChangeFilter(
                'created_at',
                `${format(date, 'yyyy-MM-dd 23:59:59')}`,
                'lt',
                'created_to'
              )
              }
              inputVariant="outlined"
              InputProps={{
                readOnly: true,
                startAdornment: (
                  <CalendarTodayIcon className="text-muted text-16" />
                ),
                endAdornment: (
                  <Icon
                    className="cursor-pointer"
                    onClick={(event) => handleClearDate(event, 'created_to')}
                  >
                    close
                  </Icon>
                )
              }}
            />
          </MuiPickersUtilsProvider>
        </Collapse>
        <Divider className="my-5" />
        <div
          className="flex items-center justify-between cursor-pointer"
          onClick={() => toggleExpandedName((expandedName) => !expandedName)}
        >
          <Typography variant="h6">Name</Typography>
          {expandedName ? <ExpandLess /> : <ExpandMore />}
        </div>
        <Collapse in={expandedName} timeout="auto">
          <TextField
            fullWidth
            variant="outlined"
            className="my-5"
            name="customer_firstname"
            label="First Name"
            value={conditions.customer_firstname.value.replace(/%/g, '')}
            onChange={(event) => {
              const {name, value} = event.target;
              handleChangeFilter(name, `%${value}%`, 'like', name);
            }}
          />
          <TextField
            fullWidth
            variant="outlined"
            className="mb-5"
            name="customer_lastname"
            label="Last Name"
            value={conditions.customer_lastname.value.replace(/%/g, '')}
            onChange={(event) => {
              const {name, value} = event.target;
              handleChangeFilter(name, `%${value}%`, 'like', name);
            }}
          />
        </Collapse>
        <Divider className="my-5" />
        <div
          className="flex items-center justify-between cursor-pointer"
          onClick={() => toggleExpandedEmail((expandedEmail) => !expandedEmail)}
        >
          <Typography variant="h6">Email</Typography>
          {expandedEmail ? <ExpandLess /> : <ExpandMore />}
        </div>
        <Collapse in={expandedEmail} timeout="auto">
          <TextField
            fullWidth
            variant="outlined"
            className="my-5"
            name="customer_email"
            label="Email"
            value={conditions.customer_email.value.replace(/%/g, '')}
            onChange={(event) => {
              const {name, value} = event.target;
              handleChangeFilter(name, `%${value}%`, 'like', name);
            }}
          />
        </Collapse>
        <Divider className="my-5" />
        <div
          className="flex items-center justify-between cursor-pointer"
          onClick={() => toggleExpandedStatus((expandedStatus) => !expandedStatus)}
        >
          <Typography variant="h6">Status</Typography>
          {expandedStatus ? <ExpandLess /> : <ExpandMore />}
        </div>
        <Collapse in={expandedStatus} timeout="auto">
          <FormControl component="div">
            <RadioGroup
              aria-label="status"
              name="status"
              value={conditions?.status?.value || ''}
              onChange={({target: {value, name}}) => handleChangeFilter(name, value, 'eq', name)}
            >
              {Object.keys(orderStatus).map((status) => (
                <FormControlLabel
                  key={`order-status-${status}`}
                  value={status}
                  control={<Radio color="primary" size="small" />}
                  label={orderStatus[status]}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Collapse>
        <Divider className="my-5" />
        {!hiddenFiltersKeys.includes('customer_group_id') &&
        <>
          <div
            className="flex items-center justify-between cursor-pointer"
            onClick={() => toggleExpandedCustomerGroup((expandedCustomerGroup) => !expandedCustomerGroup)}
          >
            <Typography variant="h6">Customer Group</Typography>
            {expandedCustomerGroup ? <ExpandLess /> : <ExpandMore />}
          </div>
          <Collapse in={expandedCustomerGroup} timeout="auto">
            <FormControl component="div">
              {[...defaultCustomerGroups, ...customerGroups].map((group) => {
                const isCustomerGroupChecked = conditions.customer_group_id.value.split(',').some((value) => value === group.id.toString());

                return (
                  <FormControlLabel
                    key={`customer-group-${group.id}`}
                    value={group.id}
                    control={
                      <Checkbox
                        color="primary"
                        size="small"
                        checked={isCustomerGroupChecked}
                        onChange={(event) => {
                          const { checked } = event.target;
                          handleChangeCustomerGroup(checked, group);
                        }}
                      />
                    }
                    label={group.code}
                  />
                );
              })}
            </FormControl>
          </Collapse>
          <Divider className="my-5" />
        </>
        }
        {isNextechIntegrated &&
        <div>
          <div
            className="flex items-center justify-between cursor-pointer"
            onClick={() => toggleExpandedSyncStatus((expandedSyncStatus) => !expandedSyncStatus)}
          >
            <Typography variant="h6">Nextech Integration</Typography>
            {expandedSyncStatus ? <ExpandLess /> : <ExpandMore />}
          </div>
          <Collapse in={expandedSyncStatus} timeout="auto">
            <FormControl>
              <RadioGroup
                aria-label="nextech"
                name="sync-status"
                value={conditions?.nextech?.value || ''}
              >
                <FormControlLabel
                  value={nextechIdTypes.sync}
                  control={
                    <Radio
                      name="nextech_id"
                      color="primary"
                      size="small"
                      onChange={({target: {value, name}}) => {
                        setConditions({...conditions,
                          nextech: {
                            field: name,
                            value,
                            conditionType: 'nin'
                          }});
                      }}
                    />}
                  label={statusMessages.synced}
                />
                <FormControlLabel
                  value={nextechIdTypes.error}
                  control={
                    <Radio
                      name="nextech_id"
                      color="primary"
                      size="small"
                      onChange={({target: {value, name}}) => {
                        setConditions({...conditions,
                          sync_error: {
                            field: 'emr_sync_error',
                            value: ' ',
                            conditionType: 'notnull'
                          },
                          nextech: {
                            field: name,
                            value,
                            conditionType: 'null'
                          }});
                      }}
                    />}
                  label={statusMessages.error}
                />
                <FormControlLabel
                  value={nextechIdTypes.disabled}
                  control={
                    <Radio
                      name="nextech_id"
                      color="primary"
                      size="small"
                      onChange={({target: {value, name}}) => {
                        setConditions({...conditions,
                          nextech: {
                            field: name,
                            value,
                            conditionType: 'in'
                          }});
                      }}
                    />}
                  label={statusMessages.disabled}
                />
                <FormControlLabel
                  value={nextechIdTypes.waitForSync}
                  control={
                    <Radio
                      name="nextech_id"
                      color="primary"
                      size="small"
                      onChange={({target: {value, name}}) => {
                        setConditions({...conditions,
                          nextech: {
                            field: name,
                            value,
                            conditionType: 'null'
                          },
                          sync_error: {
                            field: 'emr_sync_error',
                            value: ' ',
                            conditionType: 'null'
                          }});
                      }}
                    />}
                  label={statusMessages.pending}
                />
              </RadioGroup>
            </FormControl>
          </Collapse>
          <Divider className="my-5" />
        </div>
        }
        {isModMedIntegrated &&
          <div>
            <div
              className="flex items-center justify-between cursor-pointer"
              onClick={() => toggleExpandedModMedStatus((expandedModMedStatus) => !expandedModMedStatus)}
            >
              <Typography variant="h6">Mod Med Integration</Typography>
              {expandedModMedStatus ? <ExpandLess /> : <ExpandMore />}
            </div>
            <Collapse in={expandedModMedStatus} timeout="auto">
              <FormControl>
                <RadioGroup
                  aria-label="modmed"
                  name="sync-status"
                  value={conditions?.modmed?.value || ''}
                >
                  <FormControlLabel
                    value={modmedIdTypes.sync}
                    control={
                      <Radio
                        name="modmed_id"
                        color="primary"
                        size="small"
                        onChange={({target: {value, name}}) => {
                          setConditions({...conditions,
                            modmed: {
                              field: name,
                              value,
                              conditionType: 'nin'
                            }});
                        }}
                      />}
                    label={statusMessages.synced}
                  />
                  <FormControlLabel
                    value={modmedIdTypes.error}
                    control={
                      <Radio
                        name="modmed_id"
                        color="primary"
                        size="small"
                        onChange={({target: {value, name}}) => {
                          setConditions({...conditions,
                            modmed: {
                              field: name,
                              value,
                              conditionType: 'null'
                            },
                            sync_error: {
                              field: 'emr_sync_error',
                              value: ' ',
                              conditionType: 'notnull'
                            }});
                        }}
                      />}
                    label={statusMessages.error}
                  />
                  <FormControlLabel
                    value={modmedIdTypes.disabled}
                    control={
                      <Radio
                        name="modmed_id"
                        color="primary"
                        size="small"
                        onChange={({target: {value, name}}) => {
                          setConditions({...conditions,
                            modmed: {
                              field: name,
                              value,
                              conditionType: 'in'
                            }});
                        }}
                      />}
                    label={statusMessages.disabled}
                  />
                  <FormControlLabel
                    value={modmedIdTypes.waitForSync}
                    control={
                      <Radio
                        name="modmed_id"
                        color="primary"
                        size="small"
                        onChange={({target: {value, name}}) => {
                          setConditions({...conditions,
                            modmed: {
                              field: name,
                              value,
                              conditionType: 'null'
                            },
                            sync_error: {
                              field: 'emr_sync_error',
                              value: ' ',
                              conditionType: 'null'
                            }});
                        }}
                      />}
                    label={statusMessages.pending}
                  />
                </RadioGroup>
              </FormControl>
            </Collapse>
            <Divider className="my-5" />
          </div>
        }
        <div
          className="flex items-center justify-between cursor-pointer"
          onClick={() => toggleExpandedRefundStatus((expandedRefundStatus) => !expandedRefundStatus)}
        >
          <Typography variant="h6">Order refund status</Typography>
          {expandedRefundStatus ? <ExpandLess /> : <ExpandMore />}
        </div>
        <Collapse in={expandedRefundStatus} timeout="auto">
          <FormControl component="div">
            <RadioGroup
              aria-label="refund_status"
              name="refund_status"
              value={conditions?.refund_status?.value || ''}
              onChange={({target: {value, name}}) => handleChangeFilter(name, value, 'eq', name)}
            >
              <FormControlLabel
                value="full"
                control={<Radio color="primary" size="small" />}
                label="Full Refunded"
              />
              <FormControlLabel
                value="partial"
                control={<Radio color="primary" size="small" />}
                label="Partial Refunded"
              />
              <FormControlLabel
                value="no"
                control={<Radio color="primary" size="small" />}
                label="Not Refunded"
              />
            </RadioGroup>
          </FormControl>
        </Collapse>
        <Divider className="my-5" />
      </div>
      <div className="flex items-center justify-between pb-4">
        <Button
          variant="contained"
          color="primary"
          fullWidth
          className="mr-2 uppercase"
          onClick={handleApplyFilter}
          disabled={isDatesError}
        >
          Apply Filters
        </Button>
        <Button
          variant="outlined"
          fullWidth
          className="uppercase"
          onClick={() => {
            setFilterConditions(hiddenFilters);
            setOpenFilter(false);
          }}
        >
          Reset
        </Button>
      </div>
    </div>
  );
};

export default FilterDrawer;
